import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Database,
  FileText,
  Shield,
  Coins,
  Sparkles,
  ArrowRight,
  CheckCircle,
  Building2,
  Factory,
  Truck,
  Hotel,
  Building,
  BarChart3,
  Target,
  Globe,
  Users,
  TrendingUp,
  Award,
} from 'lucide-react';

// Trust Bar Partner Logos (Mock)
const partners = [
  { name: 'Partner 1', logo: 'P1' },
  { name: 'Partner 2', logo: 'P2' },
  { name: 'Partner 3', logo: 'P3' },
  { name: 'Partner 4', logo: 'P4' },
  { name: 'Partner 5', logo: 'P5' },
];

// Value Pillars
const valuePillars = [
  {
    icon: Database,
    title: 'Data Engineering',
    description: 'Build robust data pipelines, data lakes, and quality rules for ESG data integration.',
    color: 'bg-teal/10 text-teal',
  },
  {
    icon: FileText,
    title: 'Reporting & Compliance',
    description: 'GHG inventory automation, ISSB/IFRS, GRI, TCFD compliance, and audit-ready packs.',
    color: 'bg-accent/20 text-accent',
  },
  {
    icon: Sparkles,
    title: 'Credibility Engine',
    description: 'Credibility scoring, fraud-risk detection, and finance-grade reporting for investors.',
    color: 'bg-secondary/20 text-secondary',
  },
];

// Solutions Overview
const solutions = [
  {
    icon: Database,
    title: 'Sustainability Data Engineering',
    description: 'Data pipelines, data lake/warehouse, quality rules, anomaly detection.',
    href: '/solutions/data-engineering',
  },
  {
    icon: FileText,
    title: 'Sustainability Reporting',
    description: 'GHG inventory automation, regulatory compliance, audit-ready evidence.',
    href: '/solutions/reporting',
  },
  {
    icon: Shield,
    title: 'dMRV Turnkey Solution',
    description: 'MRV framework, evidence capture, verification-ready data rooms.',
    href: '/solutions/dmrv',
  },
  {
    icon: Coins,
    title: 'Tokenization & Blockchain',
    description: 'Tokenized credits, issuance registry, proof-of-origin tracking.',
    href: '/solutions/tokenization',
  },
  {
    icon: Sparkles,
    title: 'Credibility Engine',
    description: 'Credibility scoring, fraud detection, marketplace integration.',
    href: '/solutions/credibility-engine',
  },
];

// Why SusTek
const whySusTek = [
  'Finance-grade data quality and audit-ready evidence',
  'End-to-end solution from data engineering to tokenization',
  'Deep expertise across multiple industries and frameworks',
  'Co-innovation partnerships for cutting-edge solutions',
  'Proven track record with measurable outcomes',
];

// Industries
const industries = [
  { icon: Building2, name: 'Real Estate', href: '/industries/real-estate' },
  { icon: Factory, name: 'Manufacturing', href: '/industries/manufacturing' },
  { icon: Truck, name: 'Logistics', href: '/industries/logistics' },
  { icon: Hotel, name: 'Hospitality', href: '/industries/hospitality' },
  { icon: Building, name: 'Public Sector', href: '/industries/public-sector' },
];

// Stats
const stats = [
  { value: '60+', label: 'Clients' },
  { value: '33+', label: 'ESG KPIs Processed' },
  { value: '99.9%', label: 'Data Accuracy' },
  { value: '40%', label: 'Time Savings' },
];

export const Home = () => {
  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center bg-gradient-hero overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-10 w-72 h-72 bg-teal/20 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
        </div>
        
        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              <Badge variant="outline" className="mb-6 border-teal/30 text-teal bg-teal/10 backdrop-blur-sm">
                Sustainability Credibility Infrastructure
              </Badge>
              
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight mb-6">
                Engineering Trusted Sustainability Data for{' '}
                <span className="text-teal">Reporting & Finance</span>
              </h1>
              
              <p className="text-lg sm:text-xl text-white/70 mb-8 max-w-2xl mx-auto lg:mx-0">
                SusTek builds audit-ready sustainability data pipelines, reporting systems, and
                credibility engines powering dMRV and tokenized environmental assets.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link to="/book-call">
                  <Button variant="hero-primary" size="xl" className="w-full sm:w-auto group">
                    Book a Discovery Call
                    <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/about">
                  <Button variant="hero" size="xl" className="w-full sm:w-auto">
                    Download Company Deck
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right Content - Hero Image */}
            <div className="relative hidden lg:block">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-551288049-bebda4e38f71?w=800&q=80"
                  alt="Data Analytics Dashboard"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/60 to-transparent" />
              </div>
              {/* Floating Stats Card */}
              <div className="absolute -bottom-6 -left-6 bg-card/95 backdrop-blur-sm rounded-xl p-4 shadow-xl border border-border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-teal/10 flex items-center justify-center">
                    <TrendingUp className="h-5 w-5 text-teal" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Data Accuracy</p>
                    <p className="text-xl font-bold text-foreground">99.9%</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar - Hidden per user request */}
      {/* 
      <section className="py-12 bg-muted/50 border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm text-muted-foreground mb-8">
            Trusted by leading organizations worldwide
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 lg:gap-16">
            {partners.map((partner, index) => (
              <div
                key={index}
                className="w-20 h-12 flex items-center justify-center rounded-lg bg-background border border-border text-muted-foreground font-semibold text-sm"
              >
                {partner.logo}
              </div>
            ))}
          </div>
        </div>
      </section>
      */}

      {/* Value Pillars */}
      <section className="py-20 lg:py-28 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge variant="outline" className="mb-4">Our Approach</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              From Data to Credibility
            </h2>
            <p className="text-lg text-muted-foreground">
              We engineer trusted sustainability data and convert it into finance-grade credibility.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {valuePillars.map((pillar, index) => (
              <Card key={index} className="group hover-lift border-border/50 bg-card">
                <CardHeader>
                  <div className={`w-14 h-14 rounded-xl ${pillar.color} flex items-center justify-center mb-4`}>
                    <pillar.icon className="h-7 w-7" />
                  </div>
                  <CardTitle className="text-xl">{pillar.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base leading-relaxed">
                    {pillar.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-subtle">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-4xl sm:text-5xl font-bold text-primary mb-2">{stat.value}</p>
                <p className="text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Overview */}
      <section className="py-20 lg:py-28 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge variant="outline" className="mb-4">Solutions</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Comprehensive Sustainability Solutions
            </h2>
            <p className="text-lg text-muted-foreground">
              End-to-end solutions from data engineering to tokenization and credibility infrastructure.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {solutions.map((solution, index) => (
              <Link key={index} to={solution.href}>
                <Card className="h-full group hover-lift border-border/50 bg-card cursor-pointer">
                  <CardHeader>
                    <div className="w-12 h-12 rounded-lg bg-teal/10 text-teal flex items-center justify-center mb-3 group-hover:bg-teal group-hover:text-teal-foreground transition-colors">
                      <solution.icon className="h-6 w-6" />
                    </div>
                    <CardTitle className="text-lg group-hover:text-teal transition-colors">
                      {solution.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-col flex-1">
                    <CardDescription className="text-sm leading-relaxed flex-1">
                      {solution.description}
                    </CardDescription>
                    <div className="flex items-center gap-2 mt-4 text-teal text-sm font-medium">
                      Learn more
                      <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why SusTek */}
      <section className="py-20 lg:py-28 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <Badge variant="outline" className="mb-4">Why SusTek</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">
                The Partner for Finance-Grade Sustainability
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                We help organizations turn fragmented sustainability data into audit-ready reporting
                and finance-grade credibility—through data engineering, turnkey dMRV, and
                tokenization infrastructure.
              </p>
              <ul className="space-y-4">
                {whySusTek.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-teal flex-shrink-0 mt-0.5" />
                    <span className="text-foreground">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <Link to="/about">
                  <Button variant="teal" size="lg" className="group">
                    Learn More About Us
                    <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80"
                alt="Corporate Building"
                className="rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-card rounded-xl p-6 shadow-xl border border-border max-w-xs">
                <div className="flex items-center gap-4 mb-3">
                  <Award className="h-8 w-8 text-teal" />
                  <span className="font-semibold text-foreground">Industry Recognition</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Recognized as a leader in sustainability data infrastructure.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-20 lg:py-28 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge variant="outline" className="mb-4">Industries</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Deep Expertise Across Sectors
            </h2>
            <p className="text-lg text-muted-foreground">
              Tailored solutions for industry-specific sustainability challenges.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {industries.map((industry, index) => (
              <Link key={index} to={industry.href}>
                <Card className="group hover-lift border-border/50 bg-card cursor-pointer text-center p-6">
                  <div className="w-14 h-14 mx-auto rounded-xl bg-muted group-hover:bg-teal/10 flex items-center justify-center mb-4 transition-colors">
                    <industry.icon className="h-7 w-7 text-muted-foreground group-hover:text-teal transition-colors" />
                  </div>
                  <p className="font-medium text-sm text-foreground group-hover:text-teal transition-colors">
                    {industry.name}
                  </p>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Case Study */}
      <section className="py-20 lg:py-28 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <Badge variant="outline" className="mb-4">Featured Case Study</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                Manufacturing Excellence in Taiwan
              </h2>
              <p className="text-lg text-muted-foreground mb-6">
                How a leading electronics manufacturer achieved 99.9% data accuracy and 40% time
                savings in sustainability reporting with SusTek data engineering platform.
              </p>
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="bg-card rounded-xl p-4 border border-border">
                  <p className="text-3xl font-bold text-teal mb-1">40%</p>
                  <p className="text-sm text-muted-foreground">Time Savings</p>
                </div>
                <div className="bg-card rounded-xl p-4 border border-border">
                  <p className="text-3xl font-bold text-teal mb-1">99.9%</p>
                  <p className="text-sm text-muted-foreground">Data Accuracy</p>
                </div>
              </div>
              <Link to="/case-studies">
                <Button variant="teal" size="lg" className="group">
                  View Case Study
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
            <div className="order-1 lg:order-2">
              <img
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80"
                alt="Analytics Dashboard"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Co-Innovation Callout */}
      <section className="py-20 lg:py-28 bg-primary relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:40px_40px]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge variant="outline" className="mb-4 border-primary-foreground/30 text-primary-foreground bg-primary-foreground/10">
            Co-Innovation Studio
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-primary-foreground mb-6">
            Co-build with Us
          </h2>
          <p className="text-xl text-primary-foreground/80 max-w-2xl mx-auto mb-8">
            Co-build dMRV and tokenization systems that scale. Joint solution development,
            pilot-to-scale programs, and API-first services for technology partners.
          </p>
          <Link to="/partners">
            <Button size="xl" variant="secondary" className="group text-secondary-foreground">
              Explore Partnership
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
